/**
 * FEATURE A4 · 2026-05-21 · Undo toast store.
 *
 * Global zustand store for the single-slot undo toast. A new push
 * replaces the previous one (in line with the spec: "auto-dismiss em
 * 30s ou na próxima undo toast").
 *
 * Why a global store: undo is fired across the app (RDO list, budget
 * list, FVS list, …); React-tree-local state would require lifting
 * to the App root and prop-drilling. Zustand is already the auth
 * solution; reusing keeps zero-new-deps.
 */

import { create } from 'zustand';

export interface UndoToastState {
  /** Stable id for animations / aria-live de-dup. */
  id: string;
  /** Event id used to POST /v1/undo/:eventId. */
  eventId: string;
  /** Human-friendly label ("Item X excluído"). */
  label: string;
  /** Posted-at epoch ms (for countdown). */
  postedAt: number;
  /**
   * Optional callback invoked when the user clicks "Desfazer" and the
   * undo POST succeeds. Used by the calling component to invalidate
   * caches / refresh UI. NOT included in the global slot serialization
   * — function refs are transient.
   */
  onUndone?: () => void;
}

interface UndoToastStore {
  current: UndoToastState | null;
  /** Push a new toast — replaces the current one if any. */
  push: (toast: Omit<UndoToastState, 'id' | 'postedAt'>) => void;
  /** Manually dismiss the current toast. */
  dismiss: () => void;
}

export const useUndoToast = create<UndoToastStore>((set) => ({
  current: null,
  push: (toast) =>
    set({
      current: {
        ...toast,
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
        postedAt: Date.now(),
      },
    }),
  dismiss: () => set({ current: null }),
}));
